import LeadCaptureBox from "@/components/LeadCaptureBox";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, Zap, Check, ChevronRight, BarChart3, Shuffle, Trophy, TrendingUp } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";

const HOW_IT_WORKS = [
  {
    icon: Shuffle,
    title: "AI generates up to 4 variants",
    desc: "Based on your original subject line, the AI produces variations using different angles: curiosity, urgency, social proof, and benefit-led. Each under 50 characters for mobile.",
  },
  {
    icon: BarChart3,
    title: "Recipients split round-robin",
    desc: "Your list is divided equally across variants. Variant A goes to the first quarter, Variant B to the second — statistically clean distribution, no cherry-picking.",
  },
  {
    icon: TrendingUp,
    title: "MoreMail tracks opens in real time",
    desc: "As recipients open, the dashboard updates live. You can see which variant is pulling ahead within the first few hours of sending.",
  },
  {
    icon: Trophy,
    title: "Winner runs to remaining sends",
    desc: "You choose a threshold (e.g. 4 hours or 20% of list opened). Once hit, the winning subject line automatically sends to everyone who hasn't received an email yet.",
  },
];

const SUBJECT_EXAMPLES = [
  {
    original: "Our new feature is here",
    variants: [
      { label: "Curiosity", text: "You're going to want to see this", open: "41%" },
      { label: "Benefit", text: "Cut campaign setup time by 80%", open: "38%" },
      { label: "Urgency", text: "New: live now (and it's free)", open: "29%" },
      { label: "Social proof", text: "12,000 teams already use this", open: "24%" },
    ],
  },
];

const STATS = [
  { value: "4×", label: "subject variants tested per campaign" },
  { value: "26%", label: "avg open rate lift from A/B testing" },
  { value: "Auto", label: "winner sent to remaining list" },
];

export default function SubjectLineOptimisationPage() {
  return (
    <>
      <SeoHead
        title="Subject Line Optimisation — MoreMail | A/B Test Up to 4 Variants"
        description="MoreMail's AI rewrites your subject lines using open-rate data from previous sends. A/B test up to 4 variants and let MoreMail pick the winner automatically."
        url={`${SITE_URL}/subject-line-optimisation`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Features", url: `${SITE_URL}/features` },
          { name: "Subject Line Optimisation", url: `${SITE_URL}/subject-line-optimisation` },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-ink text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-coral/20 via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20">
          <div className="flex items-center gap-1.5 text-white/40 text-xs font-medium mb-8">
            <Link to="/features" className="hover:text-white/70 transition-colors">Features</Link>
            <ChevronRight size={12} />
            <span className="text-white/60">AI Suite</span>
            <ChevronRight size={12} />
            <span className="text-white/80">Subject Line Optimisation</span>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-coral/20 border border-coral/30 rounded-full text-coral text-xs font-bold mb-6">
            <Zap size={11} />
            AI Suite
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5 max-w-3xl">
            The subject line that{" "}
            <em className="text-coral not-italic">actually</em> gets opened.
          </h1>

          <p className="text-white/60 text-base sm:text-lg max-w-2xl mb-10 leading-relaxed">
            A/B test up to 4 AI-written subject line variants per campaign. MoreMail tracks opens in real time and automatically sends the winner to the rest of your list — no manual intervention needed.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <LeadCaptureBox />
            <Link
              to="/features"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full border border-white/20 text-white/70 font-semibold hover:border-white/40 hover:text-white transition-colors"
            >
              All features
            </Link>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-coral">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-3 gap-4 text-center">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display text-2xl sm:text-3xl font-bold text-white">{s.value}</div>
              <div className="text-white/70 text-xs mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Live example */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
          See it in action
        </h2>
        <p className="text-slate text-sm text-center mb-10">
          One original subject line → four AI variants, each optimised for a different trigger.
        </p>

        {SUBJECT_EXAMPLES.map((ex) => (
          <div key={ex.original} className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
            {/* Original */}
            <div className="px-6 py-4 bg-paper border-b border-black/[0.06]">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate mb-1">Your original</p>
              <p className="text-sm font-medium text-slate italic">"{ex.original}"</p>
            </div>
            {/* Variants */}
            <div className="divide-y divide-black/[0.04]">
              {ex.variants.map((v, i) => (
                <div key={v.label} className={`px-6 py-4 flex items-center gap-4 ${i === 0 ? "bg-coral/[0.03]" : ""}`}>
                  <div className={`flex-shrink-0 w-20 text-[10px] font-bold uppercase tracking-wider ${i === 0 ? "text-coral" : "text-slate/50"}`}>
                    {v.label}
                  </div>
                  <div className="flex-1 text-sm font-medium text-ink">"{v.text}"</div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-black/[0.06] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${i === 0 ? "bg-coral" : "bg-teal"}`}
                        style={{ width: v.open }}
                      />
                    </div>
                    <span className={`text-xs font-bold w-8 text-right ${i === 0 ? "text-coral" : "text-slate"}`}>{v.open}</span>
                    {i === 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 bg-coral/10 text-coral rounded-full">
                        Winner
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">How it works</h2>
          <p className="text-slate text-sm text-center mb-12">Fully automated from variant creation to winner selection.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {HOW_IT_WORKS.map((step) => (
              <div key={step.title} className="bg-paper rounded-2xl border border-black/[0.06] p-5">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-coral/10">
                    <step.icon size={18} className="text-coral" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-ink mb-1.5">{step.title}</h3>
                    <p className="text-xs text-slate leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What the AI optimises for */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-ink mb-2 text-center">
          What the AI optimises for
        </h2>
        <p className="text-slate text-sm text-center mb-10">Each variant targets a different psychological trigger.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: "Curiosity gap", desc: "Withholds just enough information to make opening feel necessary. High performer for product updates." },
            { label: "Benefit clarity", desc: "States the exact value upfront. Works best for audiences who are already warm and want to know \"what's in it for me.\"" },
            { label: "Urgency / scarcity", desc: "Time-pressure or limited availability. Use sparingly — effective only when the deadline is genuine." },
            { label: "Social proof", desc: "Numbers, names, or peer activity as the hook. Effective with cold lists or re-engagement campaigns." },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3 p-5 bg-paper rounded-2xl border border-black/[0.06]">
              <Check size={14} className="text-coral mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-sm text-ink mb-1">{item.label}</h3>
                <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      <section className="bg-white border-t border-black/[0.06]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <h2 className="font-display text-xl font-bold text-ink mb-6">Works great with</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "AI Campaign Builder", href: "/ai-campaign-builder", desc: "Generate the full campaign body alongside your winning subject line." },
              { label: "Spam Score", href: "/spam-score", desc: "Ensure your winning variant doesn't trip spam filters before it sends." },
              { label: "Follow-up Campaigns", href: "/follow-up-campaigns", desc: "Auto-target non-openers with a different subject angle." },
            ].map((f) => (
              <Link
                key={f.label}
                to={f.href}
                className="bg-paper rounded-2xl border border-black/[0.06] p-5 hover:border-coral/30 hover:shadow-md transition-all group"
              >
                <h3 className="font-bold text-sm text-ink mb-1.5 group-hover:text-coral transition-colors flex items-center justify-between">
                  {f.label} <ArrowRight size={13} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-xs text-slate leading-relaxed">{f.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-coral">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-16 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-3">
            Stop guessing. Start testing.
          </h2>
          <p className="text-white/80 text-base mb-8">
            MoreMail finds the subject line your subscribers actually want to open. Free to start.
          </p>
          <div className="flex justify-center"><LeadCaptureBox /></div>
        </div>
      </section>
    </>
  );
}
